'use client'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { SuperscaleAgentMark, type IAgentMarkPreset } from '@/components/superscale-agent-mark/superscale-agent-mark'

/**
 * lp-v3 "vision" globe — a procedural dotted sphere (ported from the Superscale Brand Studio globe
 * generator) rendered to a <canvas> and auto-rotated, with creative thumbnails that surface on its
 * surface over time. Each thumbnail spawns at a spread-out lat/lon, pops in (scale + opacity), lives
 * a few seconds, fades out, and a fresh one appears elsewhere — so the creatives are spread across
 * the globe in BOTH space and time. Dots fade in as the rotation carries them onto the front face.
 *
 * Perf: the RAF loop is paused when the section is offscreen (IntersectionObserver) or the tab is
 * hidden, and reduced-motion users get a single static frame. Content (every thumbnail <img> + its
 * alt) still ships in the SSR HTML for SEO; only the animation is client-side. Client leaf.
 */

const D2R = Math.PI / 180

// Sphere shape — matches the Brand Studio defaults (even-distributed bands).
const LAT_BANDS = 14
const LON_POINTS = 42
const TILT = 20 // degrees, fixed
const FILL = 0.9 // sphere radius as a fraction of half the canvas
const RIM_FADE = 0.62 // dots fade toward the silhouette
const APPEAR_Z = 0.26 // dots fade in over this much depth as they rotate onto the front face
const DOT_COLOR = '18, 17, 16' // #121110 rgb; drawn with rim-based alpha
const SPIN_PER_SECOND = 8 // degrees/sec — slow ambient rotation

// Thumbnail lifecycle.
const TARGET_ACTIVE = 12 // how many creatives + agents are on the globe at once
const SPAWN_INTERVAL = 500 // ms between introducing new creatives (staggers them in time)
const LIFE_MIN = 6200
const LIFE_MAX = 11000
const ENTER_MS = 520
const EXIT_MS = 760
const BASE_THUMB = 84 // px; per-creative size varies via scale
const AGENT_SIZE = 44 // px; the round agent marks that surface between the creatives

type SpherePoint = { x: number; y: number; z: number }

function buildSpherePoints(): SpherePoint[] {
    const points: SpherePoint[] = []
    for (let i = 0; i < LAT_BANDS; i++) {
        const t = i / (LAT_BANDS - 1)
        const phi = -90 + 180 * t // latitude
        const colat = 90 - phi
        const count = Math.max(1, Math.round(LON_POINTS * Math.sin(colat * D2R)))
        for (let j = 0; j < count; j++) {
            const lam = count === 1 ? 0 : (360 * j) / count
            const cphi = Math.cos(phi * D2R)
            const sphi = Math.sin(phi * D2R)
            points.push({ x: cphi * Math.sin(lam * D2R), y: sphi, z: cphi * Math.cos(lam * D2R) })
        }
    }
    return points
}

/** Rotate a unit-sphere point by spin (around Y) then tilt (around X). */
function rotatePoint(p: SpherePoint, tiltDeg: number, spinDeg: number): SpherePoint {
    const cs = Math.cos(spinDeg * D2R)
    const ss = Math.sin(spinDeg * D2R)
    const x = p.x * cs + p.z * ss
    const z0 = -p.x * ss + p.z * cs
    const ct = Math.cos(tiltDeg * D2R)
    const st = Math.sin(tiltDeg * D2R)
    const y = p.y * ct - z0 * st
    const z = p.y * st + z0 * ct
    return { x, y, z }
}

function latLonToPoint(lat: number, lon: number): SpherePoint {
    const cphi = Math.cos(lat * D2R)
    return { x: cphi * Math.sin(lon * D2R), y: Math.sin(lat * D2R), z: cphi * Math.cos(lon * D2R) }
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const smooth = (t: number) => t * t * (3 - 2 * t)
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

/** A creative image that surfaces on the globe. `aspect` is its native width / height. */
export type GlobeThumb = { src: string; alt: string; aspect: number }

/** Fit an image's native aspect into a BASE_THUMB box so it keeps its ratio (nothing is cropped). */
function thumbBox(aspect: number) {
    if (aspect >= 1) return { w: BASE_THUMB, h: Math.round(BASE_THUMB / aspect) }
    return { w: Math.round(BASE_THUMB * aspect), h: BASE_THUMB }
}

type Slot = { active: boolean; lat: number; lon: number; scale: number; born: number; life: number; hidden: boolean }

export function VisionGlobe({
    thumbs,
    agents = [],
    className,
}: {
    thumbs: GlobeThumb[]
    /** Pixel-churn agent marks that pop up on the globe exactly like the creatives. */
    agents?: IAgentMarkPreset[]
    className?: string
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const thumbRefs = useRef<(HTMLDivElement | null)[]>([])

    useEffect(() => {
        const canvas = canvasRef.current
        const container = canvas?.parentElement
        if (!canvas || !container) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const points = buildSpherePoints()
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        // One slot per surfaced element (creatives first, then agent marks) — all live in the DOM
        // (SEO); the loop cycles which are visible.
        const slots: Slot[] = [...thumbs, ...agents].map(() => ({
            active: false,
            lat: 0,
            lon: 0,
            scale: 1,
            born: 0,
            life: 0,
            hidden: true,
        }))

        let width = 0
        let height = 0
        let dpr = 1
        // set in the reduced-motion branch so a resize repaints the single static frame (the RAF
        // loop repaints on its own every frame, so it needs no such hook)
        let renderStatic: (() => void) | null = null
        const resize = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2)
            width = container.clientWidth
            height = container.clientHeight
            canvas.width = width * dpr
            canvas.height = height * dpr
            canvas.style.width = `${width}px`
            canvas.style.height = `${height}px`
            renderStatic?.()
        }
        resize()
        const resizeObserver = new ResizeObserver(resize)
        resizeObserver.observe(container)

        const rand = (a: number, b: number) => a + Math.random() * (b - a)

        // Pick a lat/lon spread away from the currently-active thumbnails so they don't clump.
        const spawnPosition = () => {
            let best = { lat: 0, lon: 0 }
            let bestDist = -1
            for (let attempt = 0; attempt < 6; attempt++) {
                const lat = rand(-52, 52)
                const lon = rand(0, 360)
                let minDist = Infinity
                for (const slot of slots) {
                    if (!slot.active) continue
                    const dLat = lat - slot.lat
                    let dLon = Math.abs(lon - slot.lon)
                    if (dLon > 180) dLon = 360 - dLon
                    const dist = dLat * dLat + dLon * dLon
                    if (dist < minDist) minDist = dist
                }
                if (minDist > bestDist) {
                    bestDist = minDist
                    best = { lat, lon }
                }
            }
            return best
        }

        const activateOne = (now: number) => {
            const dormant: number[] = []
            for (let i = 0; i < slots.length; i++) if (!slots[i].active) dormant.push(i)
            if (!dormant.length) return
            const index = dormant[Math.floor(Math.random() * dormant.length)]
            const pos = spawnPosition()
            slots[index] = {
                active: true,
                lat: pos.lat,
                lon: pos.lon,
                scale: rand(0.62, 1.04),
                born: now,
                life: rand(LIFE_MIN, LIFE_MAX),
                hidden: false,
            }
        }

        const cx = () => width / 2
        const cy = () => height / 2
        const radius = () => (Math.min(width, height) / 2) * FILL

        const drawDots = (spin: number, globalAlpha: number) => {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.clearRect(0, 0, width, height)
            const centerX = cx()
            const centerY = cy()
            const R = radius()
            const dot = R * 0.011
            ctx.fillStyle = `rgb(${DOT_COLOR})`
            for (const p of points) {
                const r = rotatePoint(p, TILT, spin)
                if (r.z < 0) continue // front hemisphere only
                const sx = centerX + R * r.x
                const sy = centerY - R * r.y
                const rim = Math.min(1, Math.hypot(sx - centerX, sy - centerY) / R)
                // fade in as the dot rotates onto the front face, then fade toward the silhouette
                const appear = smooth(clamp01(r.z / APPEAR_Z))
                const alpha = globalAlpha * appear * 0.9 * (1 - RIM_FADE * Math.pow(rim, 3))
                if (alpha <= 0.02) continue
                ctx.globalAlpha = alpha
                ctx.fillRect(sx - dot, sy - dot, dot * 2, dot * 2)
            }
            ctx.globalAlpha = 1
        }

        const positionThumbs = (now: number, spin: number, animate: boolean) => {
            const centerX = cx()
            const centerY = cy()
            const R = radius()
            const viewScale = width < 560 ? 0.6 : 1 // smaller creatives on mobile
            for (let i = 0; i < slots.length; i++) {
                const slot = slots[i]
                const el = thumbRefs.current[i]
                if (!el) continue

                if (!slot.active) {
                    if (!slot.hidden) {
                        el.style.opacity = '0'
                        slot.hidden = true
                    }
                    continue
                }

                const age = now - slot.born
                if (animate && age > slot.life) {
                    slot.active = false
                    el.style.opacity = '0'
                    slot.hidden = true
                    continue
                }

                // enter / exit envelope
                const enter = animate ? easeOut(clamp01(age / ENTER_MS)) : 1
                const exit = animate ? clamp01((slot.life - age) / EXIT_MS) : 1
                const env = Math.min(enter, exit)

                const r = rotatePoint(latLonToPoint(slot.lat, slot.lon), TILT, spin)
                const front = r.z >= -0.04
                const sx = centerX + R * r.x
                const sy = centerY - R * r.y
                const depth = (r.z + 1) / 2 // 0 (back) .. 1 (front)
                const depthScale = 0.84 + depth * 0.3
                const enterScale = 0.7 + 0.3 * enter
                const scale = slot.scale * depthScale * enterScale * viewScale

                // transform + opacity only — kept compositor-friendly (no per-frame layout/filter work)
                el.style.transform = `translate(-50%, -50%) translate(${sx}px, ${sy}px) scale(${scale})`
                el.style.opacity = front ? String(env * (0.32 + 0.68 * depth)) : '0'
                el.style.zIndex = String(Math.round(depth * 100))
                slot.hidden = false
            }
        }

        let raf = 0
        let last = 0
        let spin = 0
        let intro = 0
        let lastSpawn = -Infinity
        let running = false
        let onscreen = false

        const frame = (now: number) => {
            if (!last) last = now
            const dt = Math.min(0.05, (now - last) / 1000) // clamp so a resumed tab doesn't jump
            last = now
            spin = (spin + SPIN_PER_SECOND * dt) % 360
            intro = clamp01(intro + dt / 0.9)

            // keep TARGET_ACTIVE creatives alive, staggered over time
            let activeCount = 0
            for (const slot of slots) if (slot.active) activeCount++
            if (activeCount < TARGET_ACTIVE && now - lastSpawn > SPAWN_INTERVAL) {
                activateOne(now)
                lastSpawn = now
            }

            drawDots(spin, intro)
            positionThumbs(now, spin, true)

            raf = requestAnimationFrame(frame)
        }

        const start = () => {
            if (running) return
            running = true
            last = 0
            raf = requestAnimationFrame(frame)
        }
        const stop = () => {
            if (!running) return
            running = false
            cancelAnimationFrame(raf)
        }

        if (reduce) {
            // static: a handful of thumbnails at fixed, spread positions + one dot frame
            const fixed = [
                { lat: 12, lon: 20 },
                { lat: -20, lon: -46 },
                { lat: 30, lon: 60 },
                { lat: -8, lon: 130 },
                { lat: 24, lon: -110 },
            ]
            for (let i = 0; i < slots.length && i < fixed.length; i++) {
                slots[i] = {
                    active: true,
                    lat: fixed[i].lat,
                    lon: fixed[i].lon,
                    scale: 0.92,
                    born: 0,
                    life: Infinity,
                    hidden: false,
                }
            }
            // ...and a couple of static agent marks so reduced-motion shows them too
            const agentFixed = [
                { lat: -34, lon: 96 },
                { lat: 42, lon: -70 },
            ]
            for (let i = 0; i < agents.length && i < agentFixed.length; i++) {
                slots[thumbs.length + i] = {
                    active: true,
                    lat: agentFixed[i].lat,
                    lon: agentFixed[i].lon,
                    scale: 0.92,
                    born: 0,
                    life: Infinity,
                    hidden: false,
                }
            }
            intro = 1
            renderStatic = () => {
                drawDots(0, 1)
                positionThumbs(0, 0, false)
            }
            renderStatic()
            return () => {
                resizeObserver.disconnect()
            }
        }

        const intersection = new IntersectionObserver(
            ([entry]) => {
                onscreen = entry.isIntersecting
                if (onscreen && !document.hidden) start()
                else stop()
            },
            { threshold: 0.05 }
        )
        intersection.observe(container)

        const onVisibility = () => {
            if (document.hidden) stop()
            else if (onscreen) start()
        }
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            stop()
            intersection.disconnect()
            resizeObserver.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [thumbs, agents])

    return (
        <div className={className}>
            <div className="relative h-full w-full">
                <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden />
                {agents.map((preset, index) => (
                    <div
                        key={`agent-${preset}-${index}`}
                        ref={(el) => {
                            thumbRefs.current[thumbs.length + index] = el
                        }}
                        className="pointer-events-none absolute left-0 top-0 overflow-hidden rounded-full shadow-[0_10px_26px_rgba(19,18,16,0.18)] will-change-transform"
                        style={{ width: AGENT_SIZE, height: AGENT_SIZE, opacity: 0 }}
                    >
                        <SuperscaleAgentMark preset={preset} className="size-full rounded-full" />
                    </div>
                ))}
                {thumbs.map((thumb, index) => {
                    const box = thumbBox(thumb.aspect)
                    return (
                        <div
                            key={thumb.src}
                            ref={(el) => {
                                thumbRefs.current[index] = el
                            }}
                            className="pointer-events-none absolute left-0 top-0 overflow-hidden rounded-[10px] shadow-[0_12px_34px_rgba(19,18,16,0.2)] will-change-transform"
                            style={{ width: box.w, height: box.h, opacity: 0 }}
                        >
                            {/* box matches the image's native aspect → object-cover crops nothing */}
                            <Image src={thumb.src} alt={thumb.alt} fill sizes="96px" className="object-cover" />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
